<script setup lang="ts">
import calendarIcon from '@/assets/img/icon/calendar.svg';
import mapIcon from '@/assets/img/icon/map.svg';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import get from 'lodash/get';
import { AppButton, AppPagination } from '~/components/base';
import JobDetailDrawer from '~/components/JobDetailDrawer.vue';
import { formatDate } from '~/utils/date';

dayjs.extend(relativeTime);

interface Props {
  query?: string;
  urlPath?: string;
  location?: { place_id?: string; formatted_address?: string; description?: string };
  distance?: number | null;
  sort?: string;
  showHeading?: boolean;
  headingText?: string;
  containerClass?: string;
  emptyMessage?: string;
  emptySubtext?: string;
  showEmptyButton?: boolean;
  perPage?: number;
  showPagination?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  query: '',
  urlPath: '/',
  location: { place_id: '', formatted_address: '', description: '' },
  distance: null,
  sort: 'recent',
  showHeading: true,
  headingText: 'Jobs Added Recently',
  containerClass: 'pb-16',
  emptyMessage: 'Nothing here yet',
  emptySubtext: 'Be the first to post a listing',
  showEmptyButton: true,
  perPage: 9,
  showPagination: true,
});

const route = useRoute();
const { execute, data, error } = useGetData();
const { showSnackbar } = useSnackbar();
const page = ref(1);
const pageLength = ref(0);
const searchQuery = ref(props.query);
const loading = ref(true);
const isInitialized = ref(false);
const isFetching = ref(false);

const drawer = ref(false);
const selectedJobId = ref<number | null>(null);
const perPageValue = ref(props.perPage);

// Map sort values to backend format (pass through as-is)
const getSortParam = (sortValue: string) => {
  return sortValue || 'recent';
};

// Handle per page change
const handlePerPageChange = (newValue: number) => {
  perPageValue.value = newValue;
  page.value = 1; // Reset to first page
  fetchJobs();
};

// Watch for external prop changes
watch(
  () => props.query,
  (newQuery) => {
    searchQuery.value = newQuery;
    if (isInitialized.value) {
      page.value = 1;
      fetchJobs();
    }
  }
);

watch(
  () => props.sort,
  () => {
    if (isInitialized.value) {
      page.value = 1;
      fetchJobs();
    }
  }
);

// Watch location changes
watch(
  () => props.location,
  () => {
    if (isInitialized.value) {
      page.value = 1;
      fetchJobs();
    }
  },
  { deep: true }
);

// Watch distance changes
watch(
  () => props.distance,
  () => {
    if (isInitialized.value) {
      page.value = 1;
      fetchJobs();
    }
  }
);

// Watch page changes and fetch data
watch([page], ([newPage], [oldPage]) => {
  // Skip initial watch trigger
  if (!isInitialized.value) return;

  if (newPage !== oldPage) {
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchJobs();
  }
});

// Computed jobs list with formatted data
const jobs = computed(() => {
  if (!data.value) {
    return [];
  }
  const dataList = get(data.value, 'data');
  if (!dataList || !Array.isArray(dataList)) {
    return [];
  }

  return dataList.map((item: any) => {
    // Format timeline/deadline
    const getNeededBy = () => {
      if (item.timeline_preference === 'asap') return 'ASAP';
      if (item.timeline_preference === 'flexible') return 'Flexible';
      if (item.timeline_preference === 'choose_date' && item.timeline_date) {
        return dayjs(item.timeline_date).format('DD MMMM YYYY');
      }
      return 'Not specified';
    };

    return {
      id: item.id,
      title: item.name || 'Untitled',
      posted: item.posted_at || (item.created_at ? dayjs(item.created_at).fromNow() : 'Recently'),
      description: item.description || '',
      location: item.location_name || 'Location not specified',
      neededBy: getNeededBy(),
      postedBy: get(item, 'user.full_name') || get(item, 'user.email') || 'Anonymous',
      status: item.status?.toUpperCase() || 'OPEN',
      images: (item.photos || []).map((photo: any) => photo.url || photo),
      user: item.user,
      place_id: item.place_id,
      location_data: item.location,
      timeline_preference: item.timeline_preference,
      timeline_date: item.timeline_date,
    };
  });
});

// Fetch jobs from API
async function fetchJobs() {
  // Prevent multiple simultaneous fetches
  if (isFetching.value) {
    return;
  }

  try {
    isFetching.value = true;
    loading.value = true;

    await execute('guest/jobs', {
      params: {
        'filter[search]': searchQuery.value || undefined,
        'filter[place_id]': props.location?.place_id || undefined,
        'filter[distance]': props.distance || undefined,
        sort: getSortParam(props.sort),
        page: page.value,
        per_page: perPageValue.value,
      },
    });

    if (error.value) {
      showSnackbar({
        text: getErrorMessage(error.value),
        status: false,
      });
      return;
    }

    // Calculate pagination length
    pageLength.value = (() => {
      const meta = data.value?.meta;
      const total = meta?.total || meta?.pagination?.total;
      const perPage = meta?.per_page || meta?.pagination?.per_page || perPageValue.value;

      if (!total || total === 0) {
        return 0;
      }
      return Math.ceil(total / perPage);
    })();
  } catch (err) {
    // Only log in development to avoid DevTools performance issues
    if (import.meta.dev) {
      console.error('Failed to fetch jobs:', err);
    }
    showSnackbar({
      text: getErrorMessage(err),
      status: false,
    });
  } finally {
    loading.value = false;
    isFetching.value = false;
  }
}

const viewJob = async (job: any) => {
  selectedJobId.value = job.id;

  const urlParams = new URLSearchParams(route.query);
  urlParams.set('job', job.id.toString());
  history.pushState(null, '', `?${urlParams.toString()}`);

  nextTick(() => {
    drawer.value = true;
  });
};

// watch drawer changes: clear selected job id and remove job query param
watch(drawer, (newDrawer) => {
  if (!newDrawer) {
    selectedJobId.value = null;

    const urlParams = new URLSearchParams(route.query);
    urlParams.delete('job');
    history.pushState(null, '', `?${urlParams.toString()}`);
  }
});

const returnTo = computed(() => {
  return route.fullPath;
});

const totalResults = computed(() => {
  const meta = data.value?.meta;
  const metaTotal = meta?.total || meta?.pagination?.total;

  // If no meta, fallback to counting the jobs array
  if (!metaTotal) {
    return jobs.value?.length || 0;
  }

  return metaTotal;
});

// Expose data to parent component
defineExpose({
  jobs,
  totalResults,
  loading,
});

// Initialize on client-side only
onMounted(async () => {
  try {
    // Open job details when url query job is not null
    if (route.query.job) {
      selectedJobId.value = route.query.job as unknown as number;
      drawer.value = true;
    }

    // Initialize from query params
    page.value = Number(route.query.page) || 1;
    searchQuery.value = (route.query.search as string) || '';

    // Wait for next tick to ensure parent has set props from route params
    await nextTick();

    // Fetch initial data
    await fetchJobs();
  } catch (err) {
    // Only log in development to avoid DevTools performance issues
    if (import.meta.dev) {
      console.error('Error during component initialization:', err);
    }
  } finally {
    // Always mark as initialized even if fetch fails
    isInitialized.value = true;
  }
});
</script>

<template>
  <div :class="containerClass">
    <!-- Loading State -->
    <template v-if="loading">
      <h2 v-if="showHeading" class="text-h5 mb-7">{{ headingText }}</h2>
      <v-row>
        <v-col v-for="n in 9" :key="n" cols="12" sm="6" md="4" class="d-flex">
          <v-card flat class="d-flex flex-column w-100 pa-4">
            <v-card-text class="flex-grow-1">
              <v-skeleton-loader type="heading"></v-skeleton-loader>
              <v-skeleton-loader type="text" class="mt-2"></v-skeleton-loader>
              <v-skeleton-loader type="paragraph" class="mt-4"></v-skeleton-loader>
              <v-skeleton-loader type="list-item-two-line" class="mt-4"></v-skeleton-loader>
            </v-card-text>
            <v-card-actions>
              <v-skeleton-loader type="button" class="w-100"></v-skeleton-loader>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Error State -->
    <template v-else-if="error">
      <div class="error-state text-center py-16">
        <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
        <h3 class="text-h5 mb-2">Failed to load jobs</h3>
        <p class="text-body-1 text-grey-darken-1 mb-6">{{ getErrorMessage(error) }}</p>
        <AppButton class="px-8" @click="fetchJobs">
          <v-icon class="mr-2">mdi-refresh</v-icon>
          Retry
        </AppButton>
      </div>
    </template>

    <!-- Empty State -->
    <template v-else-if="!jobs?.length">
      <div class="empty-state text-center py-16">
        <div class="empty-icon mb-6">
          <slot name="empty-icon">
            <img src="~/assets/img/icon/empty-list.svg" alt="No jobs" />
          </slot>
        </div>
        <h3 class="text-h5 mb-2">{{ emptyMessage }}</h3>
        <p class="text-body-1 text-grey-darken-1 mb-6">{{ emptySubtext }}</p>
        <AppButton v-if="showEmptyButton" class="px-8 post_listing_btn" @click="navigateTo('/jobs/create')">
          <span class="font-bold text-subtitle-1 btn-text">Post a Listing</span>
        </AppButton>
      </div>
    </template>

    <!-- Jobs Grid -->
    <template v-else>
      <h2 v-if="showHeading" class="text-h5 mb-7">{{ headingText }}</h2>
      <v-row>
        <v-col v-for="job in jobs" :key="job.id" cols="12" sm="6" md="4" class="d-flex">
          <v-card flat class="d-flex flex-column w-100 pa-6">
            <v-card-text class="flex-grow-1 pa-0">
              <h3 class="text-h6 mb-2 job-title-clamp">{{ job.title }}</h3>
              <p class="text-grey mb-4">Posted {{ job.posted }}</p>
              <p class="mb-4 job-description-clamp">{{ job.description }}</p>
              <div class="d-flex align-center mb-2">
                <img :src="mapIcon" alt="Location" class="job-icon mr-2" />
                <span class="job-meta-text">{{ job.location }}</span>
              </div>
              <div class="d-flex align-center">
                <img :src="calendarIcon" alt="Calendar" class="job-icon mr-2" />
                <span class="job-meta-text">
                  Needed By:
                  {{
                    job.timeline_preference === 'choose_date'
                      ? formatDate(job.timeline_date, 'DD MMMM YYYY')
                      : job.neededBy
                  }}
                </span>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn block color="dark-navy" size="large" class="view-job-btn" @click="viewJob(job)"> View Job </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Pagination -->
      <AppPagination
        v-if="showPagination && pageLength > 1"
        v-model="page"
        :length="pageLength"
        :per-page="perPageValue"
        :per-page-options="[9, 18, 27, 36]"
        class="mt-8"
        @update:per-page="handlePerPageChange"
      />
    </template>

    <!-- Listing Detail Drawer -->
    <JobDetailDrawer v-model="drawer" :job-id="selectedJobId" />
  </div>
</template>

<style scoped lang="scss">
@use '#core/assets/scss/partials/fonts' as *;
@use '#core/assets/scss/partials/colors' as *;
@use 'sass:color';

h2,
h3 {
  font-family: $font-family-display !important;
  font-weight: 700;
}

// Specifically target the heading and job titles
.text-h4 {
  font-family: $font-family-display !important;
}

.job-title-clamp {
  font-family: $font-family-display !important;
}

.empty-state {
  height: 372px;

  .empty-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background-color: $background-tertiary;
    margin: 0 auto;

    img {
      width: 64px;
      height: 64px;
    }
  }

  .post_listing_btn {
    width: 172px;
  }

  .btn-text {
    line-height: 0;
  }
}

:deep(.v-card-actions) {
  padding: 0 !important;
}

/* Custom button styling for AppButton components only */
:deep(.app-button .v-btn) {
  background-color: $brand-yellow !important;
  color: $dark-navy !important;

  &:hover {
    background-color: $brand-yellow-shade-01 !important;
  }
}

/* Job card styling */
.job-card {
  border: 1px solid $border-default;
  background-color: $background-primary;
}

/* View Job button styling */
.view-job-btn {
  background-color: $dark-navy !important;
  color: $brand-yellow !important;

  &:hover {
    background-color: $dark-navy !important;
  }
}

/* Job title clamp to 1 line */
.job-title-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Job description clamp to 2 lines */
.job-description-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Job meta text styling */
.job-meta-text {
  color: #414156 !important;
}

/* Job icon styling */
.job-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}
</style>
